"use client";

import {
    ISearchEventOptions,
    ISearchOptions,
    ISportFeedResponse,
    ITopLeagueFeedResponse,
    ICategoryFeedResponse,
    ITournamentFeedResponse,
    IEventFeedResponse,
} from "@finbackoffice/websocket-client";
import { useCallback, useContext, useMemo } from "react";
import { sortAlphabeticallyByProp, sortSports } from "@finbackoffice/fe-core";
import { FeedRidContext, WebsocketContext } from "@finbackoffice/site-core";
import { ConfigContextV2 } from "contexts";
import { EventStatus } from "@finbackoffice/enums";
import { useSportFeedType } from "hooks/sportsbook";

export interface ISocketStorage {
    getTopLeagues: (rid: string) => ITopLeagueFeedResponse[];
    getCategoryById: (id: number) => ICategoryFeedResponse | null | undefined;
    getTournaments: (categoryId: number, options?: ISearchOptions) => ITournamentFeedResponse[];
    getEvents: (tournamentId: number, options?: ISearchEventOptions) => IEventFeedResponse[];
    getEventsBySportId: (sportId: number) => IEventFeedResponse[];
    getSportById: (id: number) => ISportFeedResponse | null | undefined;
    getCategories: (sportId: number, options?: ISearchOptions) => ICategoryFeedResponse[];
    getSports: (rid: string, options?: ISearchOptions) => ISportFeedResponse[];
    doesTournamentExist: (tournamentId: number, categoryId: number) => boolean;
    getSportBySlug: (slug: string) => ISportFeedResponse | null | undefined;
    hasCategory: (categoryId: number, sportId: number) => boolean;
    getTournamentById: (id: number) => ITournamentFeedResponse | null | undefined;
    getDefaultLiveGameParams: () =>
        | {
              sportSlug: string;
              categoryId: number;
              gameId: number;
          }
        | undefined;
    getDefaultSport: () => ISportFeedResponse;
    getEvent: (id: number) => IEventFeedResponse | undefined;
}

export const useSocketStorage = (rid: string): ISocketStorage => {
    const { socketClient } = useContext(WebsocketContext);
    const { sportsOrder } = useContext(ConfigContextV2);
    const { liveRid, resultsRid } = useContext(FeedRidContext);

    const { isBCFeed } = useSportFeedType();

    const getSports = useCallback(
        (rid: string, options?: ISearchOptions) => {
            if (isBCFeed) {
                return socketClient?.getSports(rid, options) ?? [];
            }
            return (
                socketClient
                    ?.getSports(rid, options)
                    .sort((a, b) => sortSports(sportsOrder)(a, b)) ?? []
            );
        },
        [isBCFeed, socketClient, sportsOrder],
    );

    const getTopLeagues = useCallback(
        (rid: string) => socketClient?.getTopLeagues(rid) ?? [],
        [socketClient],
    );

    const getSportById = useCallback(
        (id: number) => socketClient?.getSport(rid, id) || null,
        [rid, socketClient],
    );

    const getSportBySlug = useCallback(
        (slug: string) =>
            socketClient?.getSports(rid)?.find((sport) => sport.slug === slug) || null,
        [rid, socketClient],
    );

    const getCategories = useCallback(
        (sportId: number, options?: ISearchOptions) => {
            if (isBCFeed) {
                return socketClient?.getCategories(rid, sportId, options) ?? [];
            }
            return (
                socketClient
                    ?.getCategories(rid, sportId, options)
                    .sort(sortAlphabeticallyByProp("name")) ?? []
            );
        },
        [isBCFeed, rid, socketClient],
    );

    const getCategoryById = useCallback(
        (id: number) => socketClient?.getCategory(rid, id) || null,
        [rid, socketClient],
    );

    const hasCategory = useCallback(
        (categoryId: number, sportId: number): boolean =>
            getSportById(sportId)?.categories.indexOf(categoryId) !== -1 || false,
        [getSportById],
    );

    const getTournaments = useCallback(
        (categoryId: number, options?: ISearchOptions) =>
            socketClient?.getTournaments(rid, categoryId, options) ?? [],
        [rid, socketClient],
    );

    const getTournamentById = useCallback(
        (id: number) => socketClient?.getTournament(rid, id) || null,
        [rid, socketClient],
    );

    const doesTournamentExist = useCallback(
        (tournamentId: number, categoryId: number): boolean =>
            getCategoryById(categoryId)?.tournaments.indexOf(tournamentId) !== -1 || false,
        [getCategoryById],
    );

    const getEvents = useCallback(
        (tournamentId: number, options?: ISearchEventOptions) => {
            if (rid === liveRid) {
                options = options || { statuses_to_exclude: [EventStatus.NotActive] };
            } else if (rid === resultsRid) {
                options = options || { statuses_to_exclude: [] };
            }

            return socketClient?.getEvents(rid, tournamentId, options) ?? [];
        },
        [liveRid, resultsRid, rid, socketClient],
    );

    const getEventsBySportId = useCallback(
        (sportId: number) => socketClient?.getEventsBySportId(rid, sportId) ?? [],
        [rid, socketClient],
    );

    const getDefaultLiveGameParams = useCallback(() => {
        let params;
        const firstSport = getSports(rid)[0];
        if (firstSport) {
            const firstCategoryId = getCategories(firstSport.id)[0]?.id;
            const firstTournament = getTournaments(firstCategoryId)[0];
            const firstGameId = firstTournament?.events[0];

            if (firstCategoryId && firstGameId) {
                params = {
                    sportSlug: firstSport.slug,
                    categoryId: firstCategoryId,
                    gameId: firstGameId,
                };
            }
        }

        return params;
    }, [getCategories, getSports, getTournaments, rid]);

    const getDefaultSport = useCallback(() => getSports(rid)[0], [getSports, rid]);

    const getEvent = useCallback(
        (id: number) => socketClient?.getEvent(rid, id),
        [rid, socketClient],
    );

    return useMemo(
        () => ({
            getTopLeagues,
            getCategoryById,
            getTournaments,
            getEvents,
            getEventsBySportId,
            getSportById,
            getCategories,
            getSports,
            doesTournamentExist,
            getSportBySlug,
            hasCategory,
            getTournamentById,
            getDefaultLiveGameParams,
            getDefaultSport,
            getEvent,
        }),
        [
            getTopLeagues,
            getCategoryById,
            getTournaments,
            getEvents,
            getEventsBySportId,
            getSportById,
            getCategories,
            getSports,
            doesTournamentExist,
            getSportBySlug,
            hasCategory,
            getTournamentById,
            getDefaultLiveGameParams,
            getDefaultSport,
            getEvent,
        ],
    );
};
